import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ShoppingCart, ArrowLeft, Package, ShieldCheck } from 'lucide-react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { useCart } from '../context/CartContext';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import api from '../utils/api';

const ProductDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [quantity, setQuantity] = useState(1);
  const [customSize, setCustomSize] = useState('');
  const { addToCart } = useCart();
  const { user } = useAuth();

  useEffect(() => {
    fetchProduct();
  }, [id]);

  const fetchProduct = async () => {
    try {
      setLoading(true);
      const response = await api.get(`/products/${id}`);
      setProduct(response.data);
    } catch (error) {
      console.error('Error fetching product:', error);
      toast.error('Product not found');
      navigate('/products');
    } finally {
      setLoading(false);
    }
  };

  const handleAddToCart = async () => {
    if (!user) {
      toast.error('Please login to add items to cart');
      return;
    }

    try {
      await addToCart(product.id, quantity, customSize);
      toast.success(`Added ${quantity} item(s) to cart!`);
      setCustomSize('');
    } catch (error) {
      toast.error('Failed to add to cart');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <div className="flex-1 flex items-center justify-center">
          <p>Loading...</p>
        </div>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="min-h-screen flex flex-col">
      <Navbar />

      <div className="flex-1 bg-slate-50 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <Button
            variant="ghost"
            onClick={() => navigate(-1)}
            className="mb-6"
            data-testid="back-button"
          >
            <ArrowLeft className="h-4 w-4 mr-2" />
            Back
          </Button>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8" data-testid="product-detail">
            {/* Product Images */}
            <div className="bg-white rounded-sm shadow-sm overflow-hidden">
              <div className="aspect-square">
                <img
                  src={product.images?.[0] || 'https://images.unsplash.com/photo-1615746363486-92cd8c5e0a90?w=800'}
                  alt={product.name}
                  className="w-full h-full object-cover"
                  data-testid="product-image"
                />
              </div>
            </div>

            {/* Product Info */}
            <div className="space-y-6">
              <div>
                <h1 className="font-heading text-3xl lg:text-4xl font-bold text-primary mb-4 uppercase tracking-tight" data-testid="product-name">
                  {product.name}
                </h1>
                <p className="text-4xl font-bold text-secondary" data-testid="product-price">
                  ₹{product.price.toFixed(2)}
                </p>
              </div>

              <div className="border-t border-b border-slate-200 py-4">
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex items-center gap-2">
                    <Package className="h-5 w-5 text-slate-600" />
                    <span className={product.stock > 0 ? 'text-green-600 font-medium' : 'text-red-600 font-medium'} data-testid="product-stock">
                      {product.stock > 0 ? `In Stock (${product.stock} available)` : 'Out of Stock'}
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <ShieldCheck className="h-5 w-5 text-slate-600" />
                    <span className="text-slate-600">Quality Assured</span>
                  </div>
                </div>
              </div>

              <div>
                <h3 className="font-heading text-xl font-bold text-primary mb-3 uppercase">Description</h3>
                <p className="text-slate-600 leading-relaxed" data-testid="product-description">{product.description}</p>
              </div>

              {product.specifications && Object.keys(product.specifications).length > 0 && (
                <div>
                  <h3 className="font-heading text-xl font-bold text-primary mb-3 uppercase">Specifications</h3>
                  <dl className="grid grid-cols-2 gap-3">
                    {Object.entries(product.specifications).map(([key, value]) => (
                      <div key={key}>
                        <dt className="text-sm text-slate-600 font-medium">{key}</dt>
                        <dd className="text-sm text-primary">{value}</dd>
                      </div>
                    ))}
                  </dl>
                </div>
              )}

              <div className="space-y-4">
                <div>
                  <label className="text-sm font-medium mb-2 block">Quantity</label>
                  <div className="flex items-center border border-slate-300 rounded-sm w-fit">
                    <button
                      onClick={() => setQuantity(Math.max(1, quantity - 1))}
                      className="px-4 py-2 hover:bg-slate-100"
                      data-testid="decrease-quantity"
                    >
                      -
                    </button>
                    <span className="px-6 py-2 border-x border-slate-300" data-testid="quantity-value">{quantity}</span>
                    <button
                      onClick={() => setQuantity(Math.min(product.stock, quantity + 1))}
                      className="px-4 py-2 hover:bg-slate-100"
                      disabled={quantity >= product.stock}
                      data-testid="increase-quantity"
                    >
                      +
                    </button>
                  </div>
                </div>

                <div>
                  <label className="text-sm font-medium mb-2 block">Custom Size/Specification (Optional)</label>
                  <Input
                    value={customSize}
                    onChange={(e) => setCustomSize(e.target.value)}
                    placeholder="e.g., M10x50mm, 1/2 inch, 25.4mm OD, etc."
                    className="max-w-md"
                    data-testid="custom-size-input"
                  />
                  <p className="text-xs text-slate-500 mt-1">
                    Specify exact size, diameter, length, or other specifications
                  </p>
                </div>
              </div>

              <Button
                onClick={handleAddToCart}
                size="lg"
                disabled={product.stock === 0}
                className="w-full gap-2"
                data-testid="add-to-cart-detail-button"
              >
                <ShoppingCart className="h-5 w-5" />
                {product.stock === 0 ? 'Out of Stock' : 'Add to Cart'}
              </Button>
            </div>
          </div>
        </div>
      </div>

      <Footer />
    </div>
  );
};

export default ProductDetail;